'use client';

import { useTranslations } from 'next-intl';
import {
  Badge,
  Combobox,
  IconButton,
  Input,
  MetaDefault,
  Tooltip,
} from '@terra/ui-design-system';
import { ArrowUpDownIcon, Tick01Icon } from '@terra/ui-icons';
import { HStack, VStack } from '@terra/ui-styled-system';
import type { FindingEditAssetCollection } from './findingEditForm.types';

type FindingEditAssetsMultiSelectProps = {
  selectedIds: string[];
  collection: FindingEditAssetCollection;
  isSearching: boolean;
  onValueChange: (selectedIds: string[]) => void;
  onInputValueChange: (query: string) => void;
};

export function FindingEditAssetsMultiSelect({
  selectedIds,
  collection,
  isSearching,
  onValueChange,
  onInputValueChange,
}: FindingEditAssetsMultiSelectProps) {
  const t = useTranslations(
    'designSystem.findings.editDrawer.sections.assignment.fields.assets',
  );
  const selectedLabels = selectedIds
    .map(
      (id) => collection.items.find((item) => item.value === id)?.label ?? id,
    )
    .filter((label) => label.trim().length > 0);
  const placeholder =
    selectedLabels.length === 1
      ? selectedLabels[0]
      : selectedLabels.length > 1
        ? t('selectedCount', { count: selectedLabels.length })
        : t('selectAssets');

  return (
    <Combobox.Root
      collection={collection}
      value={selectedIds}
      onValueChange={(details) => onValueChange(details.value)}
      onInputValueChange={(details) => onInputValueChange(details.inputValue)}
      multiple
      closeOnSelect={false}
      size="sm"
      width="full"
    >
      <Combobox.Control>
        <Combobox.Input placeholder={placeholder} asChild>
          <Input
            size="sm"
            bg="bg.default"
            minW={0}
            pr="11"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          />
        </Combobox.Input>
        <Combobox.Trigger asChild>
          <IconButton variant="link" aria-label={t('selectAssets')} size="xs">
            <ArrowUpDownIcon size={14} />
          </IconButton>
        </Combobox.Trigger>
      </Combobox.Control>
      <Combobox.Positioner>
        <Combobox.Content maxH="300px" overflowY="auto">
          <Combobox.List>
            {isSearching ? (
              <MetaDefault p={2} textAlign="center" color="fg.muted">
                {t('searching')}
              </MetaDefault>
            ) : collection.items.length > 0 ? (
              collection.items.map((item) => (
                <Tooltip key={item.value} title={item.label}>
                  <Combobox.Item item={item} height="auto" py={2}>
                    <VStack gap={1} alignItems="flex-start" flex={1} minW={0}>
                      <HStack gap={1} flexWrap="wrap">
                        {item.type && (
                          <Badge size="sm" variant="outline">
                            {item.type.toLowerCase()}
                          </Badge>
                        )}
                        {item.method && (
                          <Badge size="sm" variant="fill" colorPalette="gray">
                            {item.method}
                          </Badge>
                        )}
                      </HStack>
                      <Combobox.ItemText
                        whiteSpace="normal"
                        wordBreak="break-all"
                        w="full"
                        fontSize="xs"
                      >
                        {item.label}
                      </Combobox.ItemText>
                    </VStack>
                    <Combobox.ItemIndicator>
                      <Tick01Icon size={14} />
                    </Combobox.ItemIndicator>
                  </Combobox.Item>
                </Tooltip>
              ))
            ) : (
              <MetaDefault p={2} textAlign="center" color="fg.muted">
                {t('enterAssetId')}
              </MetaDefault>
            )}
          </Combobox.List>
        </Combobox.Content>
      </Combobox.Positioner>
    </Combobox.Root>
  );
}
